import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, X, MailWarning, Plus } from "lucide-react";

const Chip = ({ active, children, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
            active ? "border-transparent bg-[hsl(var(--studio-primary))] text-white"
                   : "border-border text-muted-foreground hover:bg-accent"}`}>
    {children}
  </button>
);

const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{title}</Label>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

/** Pick campaign recipients by list / role / tag / company — resolved live at send time. */
export function AudiencePicker({ value, onChange, people = [], companies = [] }) {
  const [filters, setFilters] = useState({ roles: [], tags: [], companies: [], locations: [] });
  const [lists, setLists] = useState([]);
  const [preview, setPreview] = useState({ recipients: [], total: 0, with_email: 0 });
  const [search, setSearch] = useState("");

  const audience = value.audience || {};

  useEffect(() => {
    Promise.all([api.get("/contacts/filters"), api.get("/contact-lists")])
      .then(([f, l]) => { setFilters(f.data); setLists(l.data); })
      .catch(() => {});
  }, []);

  const body = useMemo(() => ({
    audience,
    recipient_person_ids: value.recipient_person_ids || [],
    recipient_company_ids: value.recipient_company_ids || [],
    excluded_person_ids: value.excluded_person_ids || [],
    excluded_company_ids: value.excluded_company_ids || [],
  }), [audience, value.recipient_person_ids, value.recipient_company_ids,
       value.excluded_person_ids, value.excluded_company_ids]);

  const refresh = useCallback((signal) => {
    api.post("/campaigns/audience/preview", body, { signal })
      .then(({ data }) => setPreview(data))
      .catch(() => {});
  }, [body]);

  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => refresh(controller.signal), 300);
    return () => { clearTimeout(t); controller.abort(); };
  }, [refresh]);

  const toggleRule = (key, item) => {
    const current = audience[key] || [];
    const next = current.includes(item) ? current.filter((x) => x !== item) : [...current, item];
    onChange({ audience: { ...audience, [key]: next } });
  };
  const toggleFlag = (key) => onChange({ audience: { ...audience, [key]: !audience[key] } });

  const exclude = (row) => {
    const idKey = row.kind === "person" ? "recipient_person_ids" : "recipient_company_ids";
    const exKey = row.kind === "person" ? "excluded_person_ids" : "excluded_company_ids";
    onChange({
      [idKey]: (value[idKey] || []).filter((id) => id !== row.id),
      [exKey]: [...new Set([...(value[exKey] || []), row.id])],
    });
  };

  const addIndividual = (item, kind) => {
    const idKey = kind === "person" ? "recipient_person_ids" : "recipient_company_ids";
    const exKey = kind === "person" ? "excluded_person_ids" : "excluded_company_ids";
    onChange({
      [idKey]: [...new Set([...(value[idKey] || []), item.id])],
      [exKey]: (value[exKey] || []).filter((id) => id !== item.id),
    });
  };

  const needle = search.trim().toLowerCase();
  const matches = needle
    ? [...people.map((p) => ({ ...p, kind: "person" })),
       ...companies.map((c) => ({ ...c, kind: "company" }))]
        .filter((i) => (i.name || "").toLowerCase().includes(needle)).slice(0, 8)
    : [];

  return (
    <div className="space-y-4 rounded-xl border border-border p-4" data-testid="audience-picker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" /> Who gets this
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" data-testid="audience-count">
            {preview.total} recipient{preview.total === 1 ? "" : "s"}
          </Badge>
          {preview.total > preview.with_email && (
            <span className="flex items-center gap-1 text-[11px] text-[hsl(34_42%_38%)] dark:text-[hsl(34_50%_72%)]"
                  data-testid="audience-no-email">
              <MailWarning className="h-3 w-3" />
              {preview.total - preview.with_email} without an email
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {lists.length > 0 && (
          <Section title="Lists">
            {lists.map((l) => (
              <Chip key={l.id} active={(audience.list_ids || []).includes(l.id)}
                    testId={`audience-list-${l.id}`}
                    onClick={() => toggleRule("list_ids", l.id)}>{l.name}</Chip>
            ))}
          </Section>
        )}
        {filters.roles?.length > 0 && (
          <Section title="Roles & company types">
            {filters.roles.map((r) => (
              <Chip key={r} active={(audience.roles || []).includes(r)}
                    testId={`audience-role-${r.replace(/\s+/g, "-")}`}
                    onClick={() => toggleRule("roles", r)}>{r}</Chip>
            ))}
          </Section>
        )}
        {filters.companies?.length > 0 && (
          <Section title="People at a company">
            {filters.companies.slice(0, 40).map((c) => (
              <Chip key={c.id} active={(audience.company_ids || []).includes(c.id)}
                    testId={`audience-company-${c.id}`}
                    onClick={() => toggleRule("company_ids", c.id)}>{c.name}</Chip>
            ))}
          </Section>
        )}
        {filters.tags?.length > 0 && (
          <Section title="Tags">
            {filters.tags.map((t) => (
              <Chip key={t} active={(audience.tags || []).includes(t)}
                    testId={`audience-tag-${t.replace(/\s+/g, "-")}`}
                    onClick={() => toggleRule("tags", t)}>{t}</Chip>
            ))}
          </Section>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Chip active={Boolean(audience.all_people)} testId="audience-all-people"
              onClick={() => toggleFlag("all_people")}>Everyone (people)</Chip>
        <Chip active={Boolean(audience.all_companies)} testId="audience-all-companies"
              onClick={() => toggleFlag("all_companies")}>Every company</Chip>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Filters stay live — anyone added to a targeted list or role later is included when this sends.
      </p>

      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Add someone specific</Label>
        <Input placeholder="Search contacts…" value={search} data-testid="audience-search"
               onChange={(e) => setSearch(e.target.value)} />
        {matches.length > 0 && (
          <div className="rounded-md border border-border p-1" data-testid="audience-search-results">
            {matches.map((m) => (
              <button type="button" key={`${m.kind}-${m.id}`} data-testid={`audience-add-${m.id}`}
                      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-accent"
                      onClick={() => { addIndividual(m, m.kind); setSearch(""); }}>
                <Plus className="h-3 w-3" /> {m.name}
                <span className="text-muted-foreground">{m.email || "no email"}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Recipients ({preview.total})
        </Label>
        <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-border p-2"
             data-testid="audience-recipients">
          {preview.recipients.length === 0 && (
            <div className="text-xs text-muted-foreground">
              Nobody yet — tick a list or role above, or search for someone.
            </div>
          )}
          {preview.recipients.map((r) => (
            <div key={`${r.kind}-${r.id}`} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate">
                {r.name}
                <span className="text-muted-foreground"> · {r.email || "no email"}</span>
              </span>
              <Button type="button" size="icon" variant="ghost" className="h-6 w-6"
                      data-testid={`audience-remove-${r.id}`} onClick={() => exclude(r)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
